import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import StatusChip from "@entrilia/shared/components/StatusChip";
import { tagColorToThemeColorMap } from "@entrilia/shared/utilities/colorHelper";
import { nextNextFrame } from "@entrilia/shared/utilities/windowHelper";
import { Stack, Typography } from "@mui/material";
import type {
  GridAggregationFunction,
  GridApiPremium,
  GridColDef,
  GridGroupingColDefOverride,
  GridGroupingColDefOverrideParams,
  GridGroupNode,
  GridPinnedRowsProp,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import {
  gridRowIdSelector,
  gridRowNodeSelector,
  isAutogeneratedRow,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import type {
  DetailColumn,
  ReportCell,
  ReportColumn,
  ReportGroupTotal,
  ReportRow,
} from "../../../api/types/widgetTypes";
import NoDataResult from "../../common/NoDataResult";
import TabularReportCellIcon from "./TabularReportCellIcon";
import { getGroupTotalValue, getReportColumnAlignment, grandTotalsRowId } from "./tabularReportGridHelpers";
import TabularReportRowDetail from "./TabularReportRowDetail";

const CellValue = ({ cell }: { cell: ReportCell }) => {
  if (cell.tagColor) {
    return <StatusChip label={cell.formattedValue} color={tagColorToThemeColorMap[cell.tagColor]} />;
  }

  return (
    <Stack>
      <Typography>{cell.formattedValue}</Typography>
      {cell.subValue && (
        <Typography variant="caption" color="textSecondary">
          {cell.subValue}
        </Typography>
      )}
    </Stack>
  );
};

const minColumnWidth = 160;

const minGroupColumnWidth = 200;

const shouldAutosizeColumns = (columnCount: number) => columnCount > 0 && columnCount < 5;

const getReportCellFormattedValue = (row: ReportRow, columnId: string): string =>
  row.cells?.[columnId]?.formattedValue ?? "";

const getColumnDefinition = (
  column: ReportColumn,
  columns: ReportColumn[],
  groupTotals: ReportGroupTotal[]
): GridColDef<ReportRow> => {
  return {
    field: column.id,
    headerName: column.title,
    // Flex widths would paint stretched across the full grid width for a frame before autosizeColumns shrinks the columns back to content size.
    flex: shouldAutosizeColumns(columns.length) ? undefined : 1,
    minWidth: minColumnWidth,
    align: getReportColumnAlignment(column),
    aggregable: column.dataType === "Numeric",
    headerAlign: getReportColumnAlignment(column),
    valueGetter: (_, row) => getReportCellFormattedValue(row, column.id),
    valueFormatter: (value) => value ?? "",
    groupingValueGetter: (_, row) => {
      if (!row.cells) {
        return null;
      }
      return getReportCellFormattedValue(row, column.id) || null;
    },
    renderCell: ({ row, rowNode, formattedValue, field, api }) => {
      if (rowNode.type === "group") {
        // The grouping column delegates rendering of the group label to this renderCell (see GridGroupingCriteriaCell),
        // in which case the label must be rendered instead of the group total.
        const isGroupLabelCell = rowNode.groupingField === column.id;
        if (!isGroupLabelCell && !rowNode.childrenExpanded) {
          return (
            <Typography sx={{ fontWeight: "bold" }}>
              {getGroupTotalValue(rowNode.id, field, columns, groupTotals, api) ?? ""}
            </Typography>
          );
        }
        return (
          <Typography noWrap title={formattedValue} sx={{ fontWeight: "bold" }}>
            {formattedValue}
          </Typography>
        );
      }

      if (rowNode.type === "footer") {
        return (
          <Typography sx={{ fontWeight: "bold" }}>
            {getGroupTotalValue(rowNode.parent, field, columns, groupTotals, api) ?? ""}
          </Typography>
        );
      }

      if (rowNode.type === "pinnedRow") {
        return <Typography sx={{ fontWeight: "bold" }}>{formattedValue}</Typography>;
      }

      if (!row.cells) {
        return null;
      }

      const cell = row.cells[column.id];
      if (cell === undefined) {
        return "-";
      }

      if (column.hasStartIcon) {
        return (
          <>
            <TabularReportCellIcon icon={cell.startIcon} />
            <CellValue cell={cell} />
          </>
        );
      }

      return <CellValue cell={cell} />;
    },
  };
};

const getColumnDefinitions = (columns: ReportColumn[], groupTotals: ReportGroupTotal[]): GridColDef<ReportRow>[] =>
  columns.map((column) => getColumnDefinition(column, columns, groupTotals));

const getRowGroupingModel = (columns: ReportColumn[]): GridRowGroupingModel => {
  return columns
    .filter((column): column is ReportColumn & { groupIndex: number } => column.groupIndex !== undefined)
    .sort((a, b) => a.groupIndex - b.groupIndex)
    .map((column) => column.id);
};

const isGroupExpandedByDefault = () => true;

const aggregationFunctions: Record<string, GridAggregationFunction<number, number>> = {
  custom: {
    label: "",
    apply: () => 0,
    columnTypes: ["string"],
  },
};

const getAggregationModel = (columns: GridColDef[]) => {
  const value: Record<string, string> = {};

  columns
    .filter((c) => c.aggregable === true)
    .forEach((c) => {
      value[c.field] = "custom";
    });

  return value;
};

const getAggregationPosition = (groupNode: GridGroupNode) => (groupNode.parent === null ? null : ("footer" as const));

const getDetailPanelHeight = () => "auto" as const;

interface Props {
  columns: ReportColumn[];
  rows: ReportRow[];
  groupTotals?: ReportGroupTotal[];
  grandTotals?: Record<string, ReportCell>;
  hasRowsWithDetails?: boolean;
  loading?: boolean;
  detailColumns?: DetailColumn[];
  apiRef: RefObject<GridApiPremium | null>;
}

const TabularReportGrid = ({
  columns,
  rows,
  groupTotals,
  grandTotals,
  hasRowsWithDetails,
  loading,
  detailColumns,
  apiRef,
}: Props) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(() => new Set());

  const gridColumns = useMemo(() => getColumnDefinitions(columns, groupTotals ?? []), [columns, groupTotals]);

  useLayoutEffect(() => {
    if (!shouldAutosizeColumns(gridColumns.length)) {
      return;
    }

    nextNextFrame(() => {
      apiRef.current?.autosizeColumns({
        includeOutliers: true,
        includeHeaders: true,
      });
    });
  }, [gridColumns, apiRef]);

  const rowGroupingModel = useMemo(() => getRowGroupingModel(columns), [columns]);

  const aggregationModel = useMemo(() => getAggregationModel(gridColumns), [gridColumns]);

  const pinnedRows = useMemo<GridPinnedRowsProp<ReportRow> | undefined>(
    () => (grandTotals ? { bottom: [{ id: grandTotalsRowId, cells: grandTotals }] } : undefined),
    [grandTotals]
  );

  const allowColumnResizing = useMemo(
    () => !hasRowsWithDetails || expandedRowIds.size === 0,
    [expandedRowIds.size, hasRowsWithDetails]
  );

  const gridInitialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: rowGroupingModel,
      },
    },
  });

  const getGroupColumnDef = useCallback(
    (params: GridGroupingColDefOverrideParams): GridGroupingColDefOverride => {
      const leafField = params.fields.length > 1 ? undefined : params.fields[0];
      const colIndex = leafField ? gridColumns.findIndex((column) => column.field === leafField) : -1;
      const hasGroupTotals = (groupTotals?.length ?? 0) > 0;

      return {
        hideDescendantCount: true,
        headerName: gridColumns[colIndex]?.headerName || " ",
        sortable: false,
        aggregable: false,
        hideable: false,
        pinnable: false,
        filterable: false,
        minWidth: minGroupColumnWidth,
        colSpan: (value, row, _column, gridApiRef) => {
          if (!value || !isAutogeneratedRow(row) || colIndex < 0) {
            return 1;
          }

          if (hasGroupTotals) {
            // A collapsed group shows its subtotals inline, so the group cell must not span over the data columns.
            const rowNode = gridRowNodeSelector(gridApiRef, gridRowIdSelector(gridApiRef, row));
            if (rowNode?.type === "group" && !rowNode.childrenExpanded) {
              return 1;
            }
          }

          return gridColumns.length - colIndex;
        },
      };
    },
    [gridColumns, groupTotals?.length]
  );

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ReportRow>) =>
      row.detailRows?.length ? (
        <TabularReportRowDetail row={row} columns={columns} columnWidths={columnWidths} detailColumns={detailColumns} />
      ) : undefined,
    [columnWidths, columns, detailColumns]
  );

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (ids: Set<GridRowId>) => {
      setExpandedRowIds(ids);

      if (apiRef.current && ids.size > 0) {
        setColumnWidths(
          Object.fromEntries(apiRef.current.getAllColumns().map((col) => [col.field, col.computedWidth]))
        );
      }
    },
    [apiRef]
  );

  if (gridColumns.length === 0) {
    return <NoDataResult />;
  }

  return (
    <DataGrid<ReportRow>
      apiRef={apiRef}
      columns={gridColumns}
      rows={rows}
      loading={loading}
      noRowsText="No data"
      disableColumnPinning
      disableColumnReorder
      disableColumnSorting
      showColumnVerticalBorder={allowColumnResizing}
      disableColumnResize={!allowColumnResizing}
      noScrollableContainer
      initialState={gridInitialState}
      rowGroupingModel={rowGroupingModel}
      aggregationModel={aggregationModel}
      pinnedRows={pinnedRows}
      aggregationFunctions={aggregationFunctions}
      rowGroupingColumnMode="single"
      groupingColDef={getGroupColumnDef}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getAggregationPosition={getAggregationPosition}
      getDetailPanelHeight={hasRowsWithDetails ? getDetailPanelHeight : undefined}
      getDetailPanelContent={hasRowsWithDetails ? getDetailPanelContent : undefined}
      onDetailPanelExpandedRowIdsChange={hasRowsWithDetails ? handleDetailPanelExpandedRowIdsChange : undefined}
      sx={(t) => ({
        border: `1px solid ${t.palette.divider}`,
        "& .MuiDataGrid-columnHeaders": {
          "& .MuiDataGrid-filler": {
            borderBottom: `1px solid ${t.palette.divider}`,
          },
        },
      })}
    />
  );
};

export default TabularReportGrid;
